import { DepartmentLabel, useCurrentDepartmentText, useDepartmentText } from 'model/DepartmentTerminology';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import DepartmentForm, { DepartmentFormData, DepartmentFormHandle } from 'components/forms/department/DepartmentForm';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { DEPARTMENTS_PAGE_PATH } from 'routes/paths';
import { notifications } from '@mantine/notifications';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import { useEffect, useRef, useState } from 'react';
import { DepartmentGetResponse } from 'api/actions/department-get/department-get-response';
import { DEPARTMENT_REDIRECT_AFTER_SAVE } from 'env';
import {
  ALL_ORGANIZATION_MODULES_DISABLED,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { formatDepartmentPath } from 'components/department/DepartmentBreadcrumb';

/**
 * Page used to edit the department.
 *
 */
export interface EditDepartmentPageProps {
  embedded?: boolean;
  departmentIdOverride?: string;
  onSaved?: () => void;
  onClose?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
}

/** Edits a department in its route or within the department-list overlay. */
export default function EditDepartmentPage({
  embedded = false,
  departmentIdOverride,
  onSaved,
  onClose,
  onDirtyChange,
  onBusyChange,
}: EditDepartmentPageProps = {}) {
  const departmentText = useDepartmentText();
  const currentDepartmentText = useCurrentDepartmentText();
  const navigate = useNavigate();
  const { departmentId: routeDepartmentId } = useParams();
  const departmentId = departmentIdOverride ?? routeDepartmentId;
  const { getAction } = useApi();
  const { getPermissionTemplate, getDefaultPermissionTemplate } = usePermissionDataProvider();
  const [department, setDepartment] = useState<DepartmentGetResponse | null>(null);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const formRef = useRef<DepartmentFormHandle>(null);
  const [moduleFlags, setModuleFlags] = useState<OrganizationModuleFlags>(ALL_ORGANIZATION_MODULES_DISABLED);
  const { confirm } = useConfirm();

  /**
   * Handles the form submission.
   */
  function onSubmit({
    departmentName,
    departmentNumber,
    streetAddress,
    city,
    zip,
    contactPersonId,
    country,
    people,
    supportsKep,
    parentDepartmentId,
  }: DepartmentFormData) {
    const departmentUpdateAction = getAction('DepartmentUpdate');

    const mappedPeople = canManageUsers
      ? Object.entries(people)
          .map(([personId, value]) => ({
            userId: Number(personId),
            checked: value.checked,
            deleted: value.deleted,
            permissions: value.permissions,
            templateId:
              getPermissionTemplate(value.templateName)?.permissionTemplateId ??
              getDefaultPermissionTemplate().permissionTemplateId,
            isNew: value.isNew,
          }))
          .map(({ userId, permissions, templateId, deleted, isNew }) => ({
            deleted,
            userId,
            permissions: permissions ?? [],
            templateId,
            isNew,
          }))
      : undefined;

    /** Persists the exact parent, including an explicit root move. */
    const save = () => {
      setSavePending(true);
      return departmentUpdateAction({
        parameters: {
          departmentId: departmentId!,
        },
        payload: {
          departmentName,
          departmentNumber: departmentNumber ?? undefined,
          streetAddress,
          city,
          zip,
          contactPersonId: !contactPersonId || contactPersonId === '' ? undefined : Number(contactPersonId),
          country,
          people: mappedPeople,
          supportsKep,
          parentDepartmentId: parentDepartmentId ? Number(parentDepartmentId) : (null as unknown as number),
        },
      })
        .then(() => {
          setSavePending(false);
          formRef.current?.markClean();
          notifications.show({
            title: currentDepartmentText('Položka úrovne bola úspešne upravená', 'Stredisko bolo úspešne upravené'),
            message: currentDepartmentText(
              `Položka úrovne ${departmentName} bola úspešne upravená`,
              `Stredisko ${departmentName} bolo úspešne upravené`
            ),
            color: SUCCESS_NOTIFICATION_COLOR,
          });

          onSaved?.();

          if (!embedded && DEPARTMENT_REDIRECT_AFTER_SAVE === 'list') {
            navigate(DEPARTMENTS_PAGE_PATH.original);
          }
        })
        .catch((error) => {
          setSavePending(false);
          panic(error);
        });
    };

    const oldParentId = department?.parentDepartmentId ? String(department.parentDepartmentId) : '';
    if (department?.hasChildren && oldParentId !== parentDepartmentId) {
      confirm({
        title: 'Presunúť celý podstrom',
        content: (
          <DepartmentLabel
            objectLabel="Zmena nadradenej položky úrovne presunie túto položku úrovne aj všetkých jej potomkov. Pokračovať?"
            departmentLabel="Zmena nadradeného strediska presunie toto stredisko aj všetkých jeho potomkov. Pokračovať?"
          />
        ),
        onConfirm: save,
      });
      return Promise.resolve();
    }

    return save();
  }

  useEffect(() => {
    if (departmentId) {
      const departmentGetAction = getAction('DepartmentGet');
      const userHasPermissionInDepartmentAction = getAction('AuthUserHasPermissionInDepartment');
      const organizationListAction = getAction('OrganizationList');

      Promise.all([
        departmentGetAction({ parameters: { departmentId } }),
        userHasPermissionInDepartmentAction({ parameters: { departmentId, permissionSlug: 'manage-users' } }),
        organizationListAction({
          query: { filters: { 'showDiscarded.eq': 0, 'permissionSlug.eq': 'manage-users' } },
        }),
      ])
        .then(([department, { hasPermission: canManageUsers }, organizations]) => {
          setDepartment(department);
          setCanManageUsers(canManageUsers);
          const organization = organizations.find(
            ({ organizationId }) => organizationId === department.organization.organizationId
          );
          setModuleFlags(
            organization
              ? {
                  priceOffersEnabled: organization.priceOffersEnabled,
                  hrEnabled: organization.hrEnabled,
                  revisionsEnabled: organization.revisionsEnabled,
                  formsEnabled: organization.formsEnabled,
                  revisionTemplatesEnabled: organization.revisionTemplatesEnabled,
                }
              : ALL_ORGANIZATION_MODULES_DISABLED
          );
        })
        .catch(panic);
    }
  }, [departmentId]);

  if (!departmentId) {
    return <Navigate to={DEPARTMENTS_PAGE_PATH.original} />;
  }

  const form = department && (
    <DepartmentForm
      ref={formRef}
      initialValues={{
        departmentName: department.departmentName,
        departmentNumber: department.departmentNumber ?? '',
        streetAddress: department.streetAddress,
        city: department.city,
        zip: department.zip,
        country: department.country,
        contactPersonId: department.contactPerson?.userId ? String(department.contactPerson.userId) : '',
        organizationId: String(department.organization.organizationId),
        hierarchyLevelSlug: (department.breadcrumbs ?? []).at(-1)?.hierarchyLevelSlug ?? '',
        parentDepartmentId: department.parentDepartmentId ? String(department.parentDepartmentId) : '',
        supportsKep: department.supportsKep,
        organizationPeople: (department.peopleInOrganization || []).reduce((acc, curr) => {
          acc[String(curr.userId)] = {
            checked: true,
            permissions: curr.permissions,
            templateName: getPermissionTemplate(curr.templateId)?.slug ?? 'no-template',
          };

          return acc;
        }, {} as { [userId: string]: { checked: boolean; permissions: number[]; templateName: string } }),
        peopleInAncestors: department.peopleInAncestors.map((person) => {
          const sourceIndex = department.breadcrumbs.findIndex(
            ({ departmentId }) => departmentId === person.sourceDepartmentId
          );

          return {
            userId: person.userId,
            permissions: person.permissions,
            templateName: getPermissionTemplate(person.templateId)?.slug ?? 'no-template',
            sourceDepartmentId: person.sourceDepartmentId,
            sourceDepartmentName: person.sourceDepartmentName,
            sourceDepartmentPath: formatDepartmentPath(department.breadcrumbs.slice(0, sourceIndex + 1)),
          };
        }),
        people: (department.people || []).reduce((acc, curr) => {
          acc[String(curr.userId)] = {
            checked: true,
            deleted: curr.deleted,
            permissions: curr.permissions,
            templateName: getPermissionTemplate(curr.templateId)?.slug ?? 'no-template',
            isNew: curr.isNew,
          };

          return acc;
        }, {} as { [userId: string]: { checked: boolean; deleted: boolean; permissions: number[]; templateName: string; isNew: boolean } }),
      }}
      readOnly={{ organizationId: true }}
      notAllowed={{ people: !canManageUsers }}
      moduleFlags={moduleFlags}
      currentDepartmentId={Number(departmentId)}
      embedded={embedded}
      selectWithinPortal={!embedded}
      onSubmit={onSubmit}
      onSecondaryClick={embedded ? onClose : undefined}
      onDirtyChange={onDirtyChange}
      onBusyChange={(busy) => onBusyChange?.(busy || savePending)}
    />
  );

  if (embedded) return form;

  return (
    <DashboardLayout
      title={departmentText(
        `Úprava položky úrovne: ${department?.departmentName ?? ''}`,
        `Úprava strediska: ${department?.departmentName ?? ''}`
      )}
      breadcrumbs={[
        { title: departmentText('Položky úrovne', 'Strediská'), link: DEPARTMENTS_PAGE_PATH.original },
        { title: department?.departmentName ?? 'Načítavanie ...' },
      ]}
    >
      {form}
    </DashboardLayout>
  );
}
